import React, { useEffect, useState } from "react";
import { Link, useRouter } from "../router";
import { useAuth } from "../auth-context";
import { api } from "../api";
import { setPendingAction } from "../pendingAction";

function formatPrice(cents, currency, billingType, billingInterval) {
  if (!cents) return "Free";
  const amount = (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  });
  return billingType === "subscription" ? `${amount} / ${billingInterval || "month"}` : amount;
}

// A single course tile: cover image, title/subtitle, price, and the
// enroll/continue action - all handled right here so a student never has to
// leave the homepage (the old dedicated course page is offline for now, and
// deliberately doesn't reveal the chapter/lesson table of contents to
// visitors who aren't enrolled yet).
function CourseCard({ course }) {
  const { user } = useAuth();
  const { navigate } = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherBusy, setVoucherBusy] = useState(false);
  const [voucherError, setVoucherError] = useState("");

  const startCheckout = async () => {
    if (!user) {
      // Remember what they were trying to do so it can be finished
      // automatically right after they log in or sign up, instead of
      // dropping them on the dashboard with nothing enrolled.
      setPendingAction({ type: "checkout", courseId: course.id, courseSlug: course.slug });
      navigate("/login");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await api.post(`/api/checkout/${course.id}`, {});
      if (result.alreadyEnrolled) {
        navigate(`/learn/${course.slug}`);
        return;
      }
      if (result.testMode) {
        navigate(result.url);
      } else {
        window.location.href = result.url;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const redeemVoucher = async (e) => {
    e.preventDefault();
    if (!voucherCode.trim()) return;
    if (!user) {
      setPendingAction({ type: "voucher", courseSlug: course.slug, code: voucherCode.trim() });
      navigate("/login");
      return;
    }
    setVoucherBusy(true);
    setVoucherError("");
    try {
      const result = await api.post(`/api/courses/${course.slug}/redeem-voucher`, { code: voucherCode.trim() });
      navigate(`/learn/${result.courseSlug}`);
    } catch (err) {
      setVoucherError(err.message);
    } finally {
      setVoucherBusy(false);
    }
  };

  return (
    <div className="course-card">
      <div
        className="course-card-cover"
        style={course.coverImageUrl ? { backgroundImage: `url(${course.coverImageUrl})` } : {}}
      >
        {!course.coverImageUrl && <span>{course.title[0]}</span>}
      </div>
      <div className="course-card-body">
        <h3>{course.title}</h3>
        <p className="muted">{course.subtitle}</p>

        <div className="course-card-actions">
          <div className="course-price">
            {formatPrice(course.priceCents, course.currency, course.billingType, course.billingInterval)}
          </div>

          {error && <div className="alert alert-error" style={{ fontSize: 13, padding: "8px 12px" }}>{error}</div>}

          {course.accessExpired ? (
            <div className="alert alert-error" style={{ fontSize: 13, padding: "8px 12px" }}>
              Your access expired. Contact us to renew.
            </div>
          ) : course.enrolled ? (
            <Link to={`/learn/${course.slug}`} className="btn btn-primary btn-block">
              Continue learning
            </Link>
          ) : (
            <button className="btn btn-primary btn-block" onClick={startCheckout} disabled={busy}>
              {busy ? "Starting checkout…" : "Enroll now"}
            </button>
          )}

          {!course.enrolled && !course.accessExpired && course.hasVoucherOption && (
            <>
              <button
                type="button"
                className="btn-link-toggle"
                onClick={() => setVoucherOpen((v) => !v)}
              >
                Have a voucher code?
              </button>
              {voucherOpen && (
                <form onSubmit={redeemVoucher} className="voucher-redeem-form">
                  <input
                    placeholder="Enter code"
                    value={voucherCode}
                    onChange={(e) => setVoucherCode(e.target.value)}
                  />
                  {voucherError && (
                    <div className="alert alert-error" style={{ fontSize: 13, padding: "8px 12px" }}>
                      {voucherError}
                    </div>
                  )}
                  <button className="btn btn-secondary btn-block btn-sm" disabled={voucherBusy}>
                    {voucherBusy ? "Redeeming…" : "Redeem voucher"}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [courses, setCourses] = useState(null);
  const [error, setError] = useState("");
  // Set by pendingAction.js when a login-triggered enroll/voucher redemption
  // couldn't be completed automatically (wrong code, course filled up, etc.)
  // - surfaced here instead of just silently failing.
  const [pendingActionError] = useState(() => new URLSearchParams(window.location.search).get("pendingActionError"));

  useEffect(() => {
    api
      .get("/api/courses")
      .then((d) => setCourses(d.courses))
      .catch((e) => setError(e.message));
  }, []);

  return (
    <div className="page">
      <section className="hero">
        <h1>Learn skills that move your career forward</h1>
        <p>Structured courses with videos, readings, downloads, and a certificate when you finish.</p>
      </section>

      {pendingActionError && (
        <div className="alert alert-error">
          Couldn't finish that automatically after logging in: {pendingActionError}
        </div>
      )}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="course-grid">
        {courses === null && !error && <p className="muted">Loading courses…</p>}
        {courses && courses.length === 0 && <p className="muted">No courses published yet.</p>}
        {courses && courses.map((course) => <CourseCard course={course} key={course.id} />)}
      </div>
    </div>
  );
}

export { formatPrice };
